
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from 'react-use';
import { useUnsplashImages, UnsplashImage } from '@/hooks/useUnsplashImages';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Search, RefreshCcw, ImageOff, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ContentLoader } from '@/components/ContentLoader';
import { ImageErrorBoundary } from '@/components/ImageErrorBoundary';

interface UnsplashImageSearchProps {
  onImageSelect?: (imageData: {
    url: string;
    description: string;
    attribution: {
      photographerName: string;
      photographerUsername: string;
      photographerUrl: string;
      unsplashUrl: string;
    };
    width: number;
    height: number;
  }) => void;
}

export function UnsplashImageSearch({ onImageSelect }: UnsplashImageSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [renderErrors, setRenderErrors] = useState<Record<string, boolean>>({});
  const [searchError, setSearchError] = useState<Error | null>(null);
  const [searchAttempts, setSearchAttempts] = useState(0);
  const searchRequestRef = useRef(0);
  const isMountedRef = useRef(true);
  const { searchImages, isLoading, error } = useUnsplashImages();

  // Set isMounted ref to false on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Debug images state
  useEffect(() => {
    if (images.length > 0) {
      console.log("UnsplashImageSearch: Current images state:", 
        `${images.length} images, first image ID: ${images[0]?.id}`);
    } else {
      console.log("UnsplashImageSearch: No images in state");
    }
  }, [images]);

  // Debounce search term to avoid excessive API requests
  useDebounce(
    () => {
      if (searchTerm.trim()) {
        setDebouncedSearchTerm(searchTerm);
        setPage(1); // Reset page when search term changes
      }
    },
    500,
    [searchTerm]
  );

  // Define fetchImages as a useCallback to prevent unnecessary recreations
  const fetchImages = useCallback(async () => {
    if (!debouncedSearchTerm) return;

    // Use a request ID to prevent race conditions
    const currentRequest = ++searchRequestRef.current;
    
    try {
      console.log(`UnsplashImageSearch: Fetching images for: ${debouncedSearchTerm}, page: ${page}, request ID: ${currentRequest}`);
      setSearchError(null);
      
      const results = await searchImages(debouncedSearchTerm, page);
      
      // Only update state if component is still mounted and this is the current request
      if (!isMountedRef.current) {
        console.log(`UnsplashImageSearch: Component unmounted, ignoring results for request ${currentRequest}`);
        return;
      }
      
      // If this is not the most recent request, ignore the results
      if (currentRequest !== searchRequestRef.current) {
        console.log(`UnsplashImageSearch: Ignoring stale results for request ${currentRequest}, current is ${searchRequestRef.current}`);
        return;
      }
      
      if (results && Array.isArray(results.results)) {
        // Check if each image has the required properties before setting state
        const validImages = results.results.filter(image => {
          const isValid = !!image && 
                        !!image.urls && 
                        typeof image.urls === 'object' &&
                        (!!image.urls.small || !!image.urls.thumb);
                        
          if (!isValid) {
            console.warn("UnsplashImageSearch: Filtered out invalid image:", image);
          }
          return isValid;
        });
        
        console.log("UnsplashImageSearch: Filtered valid images:", validImages.length);
        
        if (validImages.length === 0 && results.results.length > 0) {
          // We had results but none were valid - log this issue
          console.error("UnsplashImageSearch: All images were filtered out due to validation issues");
          setSearchError(new Error("Received malformed image data from the server"));
          
          // Increment search attempts for retry logic
          setSearchAttempts(prev => prev + 1);
          if (searchAttempts < 3) {
            // Auto-retry after a short delay (only up to 3 times)
            setTimeout(() => {
              if (isMountedRef.current) {
                console.log(`UnsplashImageSearch: Auto-retrying search (attempt ${searchAttempts + 1})`);
                fetchImages();
              }
            }, 1000);
          }
        } else {
          setImages(validImages);
          setTotalPages(results.total_pages);
          
          // Reset search attempts on success
          setSearchAttempts(0);
          
          // Reset render errors on new search
          setRenderErrors({});
        }
      } else {
        console.error("UnsplashImageSearch: Invalid results format:", results);
        setImages([]);
        setTotalPages(0);
        setSearchError(new Error("Invalid results format from the server"));
      }
    } catch (err: any) {
      // Only set error if this is still the current request and component is mounted
      if (currentRequest === searchRequestRef.current && isMountedRef.current) {
        console.error(`UnsplashImageSearch: Error searching images (request ${currentRequest}):`, err);
        setSearchError(err instanceof Error ? err : new Error(err?.message || "Unknown error"));
      }
    }
  }, [debouncedSearchTerm, page, searchImages, searchAttempts]);

  // Search for images when the debounced search term changes
  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleImageSelect = (image: UnsplashImage) => {
    if (!onImageSelect) return;
    
    console.log("UnsplashImageSearch: Selecting image:", image.id);
    
    // Validate image data before passing it to the callback
    if (!image.urls || (!image.urls.regular && !image.urls.small)) {
      console.error("UnsplashImageSearch: Cannot select image with missing URL:", image);
      toast.error("Failed to select image: Missing image URL");
      return;
    }
    
    try {
      const imageData = {
        // Fallback to small URL if regular is not available
        url: image.urls.regular || image.urls.small || "",
        description: image.description || image.alt_description || "Unsplash image",
        attribution: {
          photographerName: image.user?.name || "Unknown photographer",
          photographerUsername: image.user?.username || "unknown",
          photographerUrl: image.user?.links?.html || "https://unsplash.com",
          unsplashUrl: image.links?.html || "https://unsplash.com",
        },
        width: image.width || 0,
        height: image.height || 0,
      };
      
      onImageSelect(imageData);
      toast.success("Image selected successfully");
    } catch (err) {
      console.error("UnsplashImageSearch: Error selecting image:", err);
      toast.error("Failed to select image");
    }
  };

  // Function to handle image load errors
  const handleImageError = (imageId: string) => {
    console.error(`UnsplashImageSearch: Image load error for ID ${imageId}`);
    setRenderErrors(prev => ({ ...prev, [imageId]: true }));
  };

  // Function to reset a specific image error
  const resetImageError = (imageId: string) => {
    setRenderErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[imageId];
      return newErrors;
    });
  };

  // Function to safely render image with multiple fallbacks
  const renderImage = (image: UnsplashImage) => {
    // Check if we've had a render error for this image
    if (renderErrors[image.id]) {
      return (
        <div className="w-full h-full bg-slate-200 flex flex-col items-center justify-center text-sm text-slate-500">
          <AlertCircle className="h-5 w-5 mb-1" />
          <span>Image failed to load</span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-1 h-6 text-xs" 
            onClick={() => resetImageError(image.id)}
          >
            <RefreshCcw className="h-3 w-3 mr-1" /> Try again
          </Button>
        </div>
      );
    }
    
    // Check if image has valid URLs
    if (!image.urls || (!image.urls.small && !image.urls.thumb)) {
      console.error("UnsplashImageSearch: Image missing valid URLs:", image);
      return (
        <div className="w-full h-full bg-slate-200 flex flex-col items-center justify-center text-sm text-slate-500">
          <ImageOff className="h-5 w-5 mb-1" />
          <span>No image</span>
        </div>
      );
    }
    
    // Use multiple URL options with fallbacks
    const primaryImageUrl = image.urls.small || "";
    const fallbackUrl = image.urls.thumb || "https://via.placeholder.com/300x200?text=Image+Not+Available";
    
    return (
      <ImageErrorBoundary 
        onRetry={() => resetImageError(image.id)}
        className="w-full h-full"
      >
        <img 
          src={primaryImageUrl} 
          alt={image.description || image.alt_description || "Unsplash image"} 
          className="w-full h-full object-cover"
          onError={() => {
            console.log(`UnsplashImageSearch: Primary image failed, trying fallback for ID ${image.id}`);
            // We'll let the ImageErrorBoundary handle this error
            handleImageError(image.id);
          }}
          loading="lazy"
          crossOrigin="anonymous"
        />
      </ImageErrorBoundary>
    );
  };

  const handleRetry = () => {
    // Clear any errors and retry the search
    setRenderErrors({});
    setSearchError(null);
    setSearchAttempts(0);
    // Force a new search by incrementing the request counter
    searchRequestRef.current++;
    // Re-trigger the search
    fetchImages();
  };

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input 
            type="search" 
            placeholder="Search for images on Unsplash..." 
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {(error || searchError) && (
          <Alert variant="destructive">
            <AlertDescription>
              {error || searchError?.message || "Error searching images"}
            </AlertDescription>
            <Button
              variant="outline"
              size="sm" 
              className="mt-2"
              onClick={handleRetry}
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </Alert>
        )}

        <ContentLoader isLoading={isLoading} error={searchError}>
          {images.length === 0 && debouncedSearchTerm && !error && !searchError ? (
            <div className="py-8 text-center">
              <p className="text-slate-500">No images found for "{debouncedSearchTerm}"</p>
              <p className="text-slate-400 text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {images.map((image) => (
                <Card 
                  key={image.id} 
                  className="overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                  onClick={() => handleImageSelect(image)}
                >
                  <div className="aspect-[16/9] relative group">
                    {renderImage(image)}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs p-2 text-center">
                      <p className="line-clamp-3">
                        {image.description || image.alt_description || "Select this image"}
                      </p>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-xs truncate text-slate-600">
                      by {image.user?.name || "Unknown"}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </ContentLoader>

        {images.length > 0 && totalPages > 1 && (
          <div className="flex justify-between mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1 || isLoading}
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-500 self-center">
              Page {page} of {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        )}

        {images.length > 0 && (
          <div className="text-xs text-center text-slate-500 py-2">
            <p>
              Images from{" "}
              <a 
                href="https://unsplash.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-slate-700"
              >
                Unsplash
              </a>
            </p>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
